<template>
    <div
        class="product-selection"
        role="group"
    >
        <DFormRow
            :label="__('products.products.form.productGroup')"
            :is-required="required"
            :error="errors.product_group_id"
        >
            <DFieldLoading
                v-if="isLoadingProducts"
                :is-loading="isLoadingProducts"
            />
            <div
                v-else
                style="display: flex; align-items: center; width: 100%;"
            >
                <DSelect
                    id="dropdown-product-group"
                    v-model="productGroupValue"
                    style="flex-grow: 1"
                    :options="productGroups"
                    :disabled="disabled || isLoadingContractOptions"
                    :placeholder="__('products.products.form.select_group')"
                    @change="handleGroupChange()"
                />
                <div style="display: flex; align-items: center; flex-shrink: 0; margin-left: 10px">
                    <DSwitch
                        v-model="displayRetiredProducts"
                        color="success"
                        variant="3d"
                        :disabled="disabled || isLoadingContractOptions || isLoadingProducts"
                        @update:checked="handleRetiredChange"
                    />
                    <label style="margin-bottom: 0; padding-left: 5px;">
                        {{ __('products.products.form.also_display_retired_products') }}
                    </label>
                </div>
            </div>
        </DFormRow>
        <DFormRow
            :label="__('customers.reseller_products.form.product')"
            :is-required="required"
            :error="errors.product_id"
        >
            <DFieldLoading
                v-if="isLoadingContractOptions"
                :is-loading="isLoadingContractOptions"
            />
            <div
                v-else
            >
                <VueSelect
                    v-model="productValues"
                    input-id="dropdown-product"
                    :multiple="multiple"
                    :close-on-select="!multiple"
                    :options="productsList"
                    :filterable="false"
                    :disabled="disabled || productGroupValue === '0' || isLoadingContractOptions || isLoadingProductsForGroup"
                    :searchable="!isLoadingProducts"
                    :selectable="optionsSelectable"
                    :clearable="false"
                    @open="onOpen"
                    @close="onClose"
                    @search="updateSearchText"
                    @option:selected="emitValue"
                    @option:deselected="emitValue"
                >
                    <template #list-footer>
                        <li
                            v-show="hasNextPage"
                            ref="load"
                            class="loader"
                        >
                            {{ __('messages.loading_more_options') }}
                        </li>
                    </template>
                    <template #no-options="{ searching }">
                        <template v-if="searching">
                            {{ __('messages.no_results') }}
                        </template>
                        <em
                            v-else
                            style="opacity: 0.5"
                        >
                            {{ __('messages.start_typing') }}
                        </em>
                    </template>
                </VueSelect>
            </div>
        </DFormRow>
    </div>
</template>

<script>

import VueSelect from 'vue-select'
import DFieldLoading from '~@/Components/Common/DFieldLoading'

export default {
    name: 'ProductSelection',
    components: { DFieldLoading, VueSelect },
    props: {
        multiple: {
            type: Boolean,
            default: true
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        selectedProductGroup: {
            type: Number,
            default: 0
        },
        selectedProducts: {
            type: Array,
            default: () => { return [] }
        },
        required: {
            type: Boolean,
            default: false,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false,
            required: false
        },
        isLoadingContractOptions: {
            type: Boolean,
            default: false
        },
        retired: {
            type: Boolean,
            default: false,
            required: false
        },
        excludeModules: {
            type: Array,
            default: () => [],
            required: false
        },
        marketRegionIds: {
            type: Array,
            default: () => [],
            required: false
        }
    },
    emits: ['product-group-update', 'update', 'reset-product-id'],
    data () {
        return {
            displayRetiredProducts: this.retired,
            productGroupValue: String(this.selectedProductGroup),
            productGroups: [],
            productValues: this.selectedProducts,
            products: [],
            observer: null,
            searchText: null,
            currentPage: 1,
            lastPage: 1,
            timeout: null,
            isLoadingProducts: false,
            isLoadingProductsForGroup: false,
            optionsSelectable: () => {
                return false
            }
        }
    },
    computed: {
        hasNextPage () {
            return this.currentPage < this.lastPage
        },
        productsList () {
            return this.products
        }
    },
    mounted () {
        this.observer = new IntersectionObserver(this.appendProducts)
    },
    created () {
        // If contract is not pre-live, show retired products by default
        if (this.disabled === true) {
            this.displayRetiredProducts = true
        }

        this.fetchProductGroups()

        if (this.productGroupValue !== '0') {
            this.fetchProductsInitially()
        }

        if (this.disabled) {
            this.products = this.selectedProducts
        }
    },
    methods: {
        async onOpen () {
            if (this.hasNextPage) {
                await this.$nextTick()
                this.observer.observe(this.$refs.load)
            }
            this.optionsSelectable = () => true
        },
        onClose () {
            this.observer.disconnect()
            this.optionsSelectable = () => false
        },
        async fetchProductsInitially () {
            if (this.productGroupValue === '0') {
                return
            }

            this.isLoadingProducts = true
            this.currentPage = 1

            await this.fetchProducts()
                .then((res) => {
                    this.lastPage = res.data.lastPage
                    this.products = res.data.items
                    this.isLoadingProducts = false
                    this.optionsSelectable = () => true
                })
        },
        async appendProducts ([{ isIntersecting, target }]) {
            if (isIntersecting) {
                if (this.productGroupValue === '0') {
                    return
                }

                const ul = target.offsetParent
                const scrollTop = target.offsetParent.scrollTop

                this.currentPage++

                this.isLoadingProducts = true

                await this.fetchProducts()
                    .then((res) => {
                        this.products = this.products.concat(res.data.items)
                        this.isLoadingProducts = false
                    })

                await this.$nextTick()
                ul.scrollTop = scrollTop
            }
        },
        fetchProducts () {
            const params = {
                productGroup: this.productGroupValue,
                retired: this.displayRetiredProducts ? 1 : 0,
                page: this.currentPage,
                oneTime: null,
                exclude_modules: this.excludeModules
            }

            if (this.marketRegionIds.length > 0) {
                params.market_region_ids = this.marketRegionIds
            }

            if (this.searchText) {
                params.query = this.searchText
            }

            return window.axios.get(this.$route('products.dropdown.selection_list', params))
        },
        updateSearchText (query) {
            this.searchText = query

            if (this.timer) {
                clearTimeout(this.timer)

                this.timer = null
            }

            this.timer = setTimeout(() => {
                this.fetchProductsInitially()
            }, 500)
        },
        handleGroupChange () {
            this.productValues = []

            this.$emit('product-group-update', this.productGroupValue)
            this.$emit('update', {
                value: '',
                plans: [],
                retired: false,
                lastPage: 1
            })

            this.isLoadingProductsForGroup = true
            this.fetchProductsInitially().then(() => {
                this.isLoadingProductsForGroup = false
            })
        },
        handleRetiredChange (value) {
            this.displayRetiredProducts = value
            this.fetchProductGroups()
            this.fetchProductsInitially()
        },
        emitValue () {
            this.$emit('update', this.productValues)
        },
        fetchProductGroups () {
            window.axios.get(this.$route('products.dropdown.group_selection_list', {
                retired: this.displayRetiredProducts ? 1 : 0,
                oneTime: null
            }))
                .then((res) => {
                    this.productGroups = res.data
                    if (this.productGroups.find(product => product.value === this.productGroupValue) === undefined) {
                        this.products = []
                        this.productValues = []
                        this.productGroupValue = '0'
                        this.$emit('reset-product-id')
                    }
                })
        }
    }
}
</script>

<style>
.product-selection {
    display: grid;
    grid-gap: 15px;
}
:root {
    --vs-selected-color: #5c6873 !important;
    --vs-state-disabled-bg: #e4e7ea !important;
    --vs-border-color: rgb(228, 231, 234) !important;
}
</style>
